import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { LicenseInfo } from "@mui/x-license-pro";
import { SnackbarProvider } from "./contexts/SnackbarContext";
import { FreyrLibraryProvider } from "./context/FreyrLibraryContext";
import { BrandingProvider } from "./branding";
import { getApiBaseUrl, getHostApiBaseUrl } from "./config/env";
import {
  getAuthorizationHeader,
  getDomain,
  getTenantId,
} from "./services/apiClient";
import HomePage from "./pages/HomePage";
import AiPlanPage from "./pages/AiPlanPage";
import {
  AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH,
  AI_PLAN_ADD_ITEMS_PATH,
  AI_PLAN_EDIT_ENHANCED_TAGGING_RULE_PATH,
  AI_PLAN_PATH,
} from "./pages/AiPlanPage/constants";
import AddAssessmentPlanItemsPage from "./pages/AiPlanPage/TaggingPlan/AddAssessmentPlanItemsPage";
import AddEnhancedTaggingRulePage from "./pages/AiPlanPage/EnhancedTaggingRule/AddEnhancedTaggingRulePage";
import NewRegulationDetails from "./pages/NewRegulationDetails";
import GlobalSearchPage from "./pages/GlobalSearchPage";
import AssessmentCenterPage from "./pages/AssessmentCenterPage";
import UrlDecodePage from "./pages/UrlDecodePage";
import { useUserPrivileges } from "./hooks/useUserPrivileges";
import { useModuleDisplayName } from "./hooks/useModuleDisplayName";
import "./index.css";

/**
 * Mounts once under <BrowserRouter> so that hitting any route directly
 * (e.g. refreshing /assessment-center) still triggers the one-time
 * `GET /authz/privileges` fetch into the zustand store. Without this,
 * `useHasPrivilege` returns `false` until the user navigates through a
 * page (HomePage / NewRegulationDetails) that calls the hook itself.
 */
function PrivilegesBootstrap({
  children,
}: Readonly<{ children: React.ReactNode }>) {
  useUserPrivileges();
  // Warm /global/config and sync document.title from module_display_name.
  useModuleDisplayName();
  return <>{children}</>;
}

const queryClient = new QueryClient();
type BrowserProcessShim = {
  env: Record<string, string | undefined>;
};

const globalWithProcess = globalThis as typeof globalThis & {
  process?: BrowserProcessShim;
};
const browserProcess = globalWithProcess.process ?? { env: {} };
const commonPackageApiPrefix = getApiBaseUrl().replace(/\/$/, "");
const hostApiBaseUrl = getHostApiBaseUrl().replace(/\/$/, "");
browserProcess.env = {
  ...browserProcess.env,
  NODE_ENV: import.meta.env.MODE,
  IM_PREFIX: commonPackageApiPrefix,
  REACT_APP_API_GETWAY: hostApiBaseUrl,
};
globalWithProcess.process = browserProcess;

const muiLicenseKey =
  import.meta.env.VITE_MUI_LICENSE_KEY ??
  "973425b63aaa040dd062fbfab76187cfTz0xMjE5ODMsRT0xNzk1MDQ2Mzk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y";

if (muiLicenseKey) {
  LicenseInfo.setLicenseKey(muiLicenseKey);
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("#root container not found");
}

/**
 * Vite/@vitejs/plugin-react will re-execute modules on HMR. Using a singleton
 * root avoids calling `createRoot()` twice on the same DOM node (which throws
 * and can cascade into removeChild NotFoundError during commit).
 */
const win = globalThis as typeof globalThis & {
  __GRI_REACT_ROOT__?: ReturnType<typeof ReactDOM.createRoot>;
};
const root = win.__GRI_REACT_ROOT__ ?? ReactDOM.createRoot(container);
win.__GRI_REACT_ROOT__ = root;

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrandingProvider>
        <FreyrLibraryProvider
          language={navigator.language || "en"}
          token={getAuthorizationHeader()}
          tenant={getTenantId()}
          domain={getDomain()}
          domainName={getDomain()}
          envVars={{
            IM_PREFIX: commonPackageApiPrefix,
            URF_PREFIX: hostApiBaseUrl,
          }}
        >
          <SnackbarProvider>
            <BrowserRouter>
              <PrivilegesBootstrap>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path={AI_PLAN_PATH} element={<AiPlanPage />} />
                  <Route
                    path={AI_PLAN_ADD_ITEMS_PATH}
                    element={<AddAssessmentPlanItemsPage />}
                  />
                  <Route
                    path={AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH}
                    element={<AddEnhancedTaggingRulePage />}
                  />
                  <Route
                    path={AI_PLAN_EDIT_ENHANCED_TAGGING_RULE_PATH}
                    element={<AddEnhancedTaggingRulePage />}
                  />
                  <Route
                    path="/assessment-center"
                    element={<AssessmentCenterPage />}
                  />
                  {/* Task-entry (read-only) mode: workflow UUID + task ID in URL */}
                  <Route
                    path="/regulation-details/task/:workflowUuid/:taskId"
                    element={<NewRegulationDetails />}
                  />
                  {/* Email / Fusion modulePath: version lives in the path so
                      shell query-string mangling cannot drop it. */}
                  <Route
                    path="/regulation-details/:id/version/:version"
                    element={<NewRegulationDetails />}
                  />
                  <Route
                    path="/regulation-details/:id"
                    element={<NewRegulationDetails />}
                  />
                  <Route
                    path="/RegulationNextGlobalSearch"
                    element={<GlobalSearchPage />}
                  />
                  <Route path="/url-decode" element={<UrlDecodePage />} />
                </Routes>
              </PrivilegesBootstrap>
            </BrowserRouter>
          </SnackbarProvider>
        </FreyrLibraryProvider>
      </BrandingProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
